/**
 * Saved stat-card-order application for descriptor-driven grids.
 *
 * A user's drag-reorder of the summary cards above a grid persists as an
 * ordered card-id list (user preference
 * `grid.<persistKey>.ct<customerTypeId>.statCardOrder.v1`);
 * this module re-applies it to the descriptor's card array. The descriptor
 * already arrives in the order the tenant's own configuration resolved to, so
 * the saved list is a personal override laid over that, never a replacement
 * for it.
 *
 * Contains:
 * - applyStatCardOrder — pure normalized-card reorder (named export)
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Reorder a normalized stat-card array by a saved ordered card-id list. Cards
 * absent from the saved order — a card the grid gained, or one a customer type
 * was granted, after the user last saved — append after the known ones in
 * descriptor order, so a new card surfaces instead of hiding behind a stale
 * preference.
 *
 * @param {Array<{cardId: string}>} cards - normalized cards, in descriptor order
 * @param {?Array<string>} orderedCardIds - saved card-id order, or null/empty for none
 * @returns {Array<{cardId: string}>} A new array, or `cards` itself when the
 *   saved order has nothing to say about it.
 */
export function applyStatCardOrder(cards, orderedCardIds) {
  if (!Array.isArray(orderedCardIds) || orderedCardIds.length === 0) return cards;
  const rank = new Map(orderedCardIds.map((cardId, index) => [cardId, index]));
  const known = cards.filter(card => rank.has(card.cardId));
  if (known.length === 0) return cards;
  known.sort((a, b) => rank.get(a.cardId) - rank.get(b.cardId));
  const unknown = cards.filter(card => !rank.has(card.cardId));
  return [...known, ...unknown];
}
